import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Hand, Globe, Check } from 'lucide-react';
const LANGUAGES = [{
  code: 'en',
  label: 'English',
  display: 'EN'
}, {
  code: 'my',
  label: 'Melayu',
  display: 'MY'
}, {
  code: 'zh',
  label: '简体中文',
  display: '中文'
}];
interface HeaderProps {
  showPulse?: boolean;
}
export function Header({
  showPulse = false
}: HeaderProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('app-language') || 'en';
  });
  const dropdownRef = useRef<HTMLDivElement>(null);
  // Listen for storage changes to update language if changed from modal
  useEffect(() => {
    const handleStorageChange = () => {
      const storedLang = localStorage.getItem('app-language');
      if (storedLang) {
        setLanguage(storedLang);
      }
    };
    window.addEventListener('storage', handleStorageChange);
    // Custom event for same-window updates
    window.addEventListener('language-change', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('language-change', handleStorageChange);
    };
  }, []);
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  const handleLanguageChange = (code: string) => {
    setLanguage(code);
    localStorage.setItem('app-language', code);
    window.dispatchEvent(new Event('language-change'));
    setIsOpen(false);
  };
  const currentLang = LANGUAGES.find(l => l.code === language) || LANGUAGES[0];
  return <header className="bg-white border-b border-gray-100 sticky top-0 z-50" data-id="element-17">
      <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between" data-id="element-18">
        <Link to="/" className="flex items-center gap-2 group" data-id="element-19">
          <div className="bg-teal-600 p-1.5 rounded-lg text-white group-hover:bg-teal-700 transition-colors" data-id="element-20">
            <Hand className="w-5 h-5" data-id="element-21" />
          </div>
          <div data-id="element-22">
            <h1 className="font-bold text-gray-900 text-lg leading-none" data-id="element-23">
              Hands
            </h1>
            <p className="text-xs text-gray-500 font-medium" data-id="element-24">Marketplace</p>
          </div>
        </Link>

        <div className="relative" ref={dropdownRef} data-id="element-25">
          <div className="relative" data-id="element-26">
            {showPulse && <span className="absolute inset-0 rounded-lg animate-ping bg-teal-400 opacity-75 duration-1000" data-id="element-27"></span>}
            <button onClick={() => setIsOpen(!isOpen)} className={`relative flex items-center gap-2 px-3 py-2 rounded-lg transition-all duration-200 ${isOpen ? 'bg-teal-50 text-teal-700' : showPulse ? 'bg-teal-50 text-teal-700 ring-2 ring-teal-500 ring-offset-2' : 'hover:bg-gray-50 text-gray-600 hover:text-teal-600'}`} aria-label="Select language" aria-expanded={isOpen} data-id="element-28">
              <Globe className={`w-4 h-4 ${showPulse ? 'animate-bounce' : ''}`} data-id="element-29" />
              <span className="text-sm font-medium" data-id="element-30">{currentLang.display}</span>
            </button>
          </div>

          {isOpen && <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-gray-100 py-1 animate-in fade-in zoom-in-95 duration-200 origin-top-right z-50" data-id="element-31">
              {LANGUAGES.map(lang => <button key={lang.code} onClick={() => handleLanguageChange(lang.code)} className={`w-full text-left px-4 py-2.5 text-sm flex items-center justify-between hover:bg-gray-50 transition-colors ${language === lang.code ? 'text-teal-600 font-medium bg-teal-50/50' : 'text-gray-700'}`} data-id="element-32">
                  <span data-id="element-33">{lang.label}</span>
                  {language === lang.code && <Check className="w-3.5 h-3.5 text-teal-600" data-id="element-34" />}
                </button>)}
            </div>}
        </div>
      </div>
    </header>;
}